import { useState } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Sparkles, AlertTriangle, TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function VideoAnalysisCard({ videoId, existingAnalysis = null }) {
  const [analysis, setAnalysis] = useState(existingAnalysis);
  const [loading, setLoading] = useState(false);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/videos/${videoId}/analyze`);
      setAnalysis(res.data);
      toast.success('AI analysis complete!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analysis failed — try again later');
    } finally {
      setLoading(false);
    }
  };

  if (!analysis && !loading) {
    return (
      <button
        onClick={runAnalysis}
        className="flex items-center gap-1.5 text-xs text-purple-500 hover:text-purple-700 font-semibold mt-1.5 transition-colors"
        data-testid={`analyze-video-${videoId}`}
      >
        <Sparkles className="w-3.5 h-3.5" /> AI Skill Analysis
      </button>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 mt-2 text-xs text-purple-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing video frames...
      </div>
    );
  }

  const score = analysis.score || 0;
  const scoreColor = score >= 7 ? 'text-green-600 bg-green-50 border-green-200' : score >= 4 ? 'text-amber-600 bg-amber-50 border-amber-200' : 'text-red-600 bg-red-50 border-red-200';

  return (
    <div className="mt-2 rounded-xl border border-purple-100 bg-purple-50/50 p-3 space-y-2" data-testid={`analysis-${videoId}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-purple-500" />
          <span className="text-xs font-bold text-purple-700">AI Analysis</span>
        </div>
        <span className={`text-sm font-extrabold px-2 py-0.5 rounded-lg border ${scoreColor}`}>
          {score}/10
        </span>
      </div>

      {analysis.observations && (
        <p className="text-xs text-slate-600 leading-relaxed">{analysis.observations}</p>
      )}

      {analysis.improvements && (
        <div className="flex items-start gap-1.5">
          <TrendingUp className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">{analysis.improvements}</p>
        </div>
      )}

      {analysis.comparison && (
        <div className="flex items-start gap-1.5">
          <TrendingUp className="w-3 h-3 text-green-500 mt-0.5 shrink-0" />
          <p className="text-xs text-green-700">{analysis.comparison}</p>
        </div>
      )}

      <div className="flex items-start gap-1.5 pt-1 border-t border-purple-100">
        <AlertTriangle className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-[10px] text-slate-400 italic">AI analysis is a tool and may not be fully accurate. Always rely on instructor expertise.</p>
      </div>
    </div>
  );
}

export function SkillProgressChart({ skillData }) {
  // skillData = [{session_number, score, observations, comparison}]
  if (!skillData || skillData.length === 0) return null;

  const maxScore = 10;
  const latest = skillData[skillData.length - 1];
  const first = skillData[0];
  const improvement = latest.score - first.score;

  return (
    <div className="rounded-xl border border-purple-100 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-500">AI Score Trend</span>
        {skillData.length > 1 && (
          <span className={`text-xs font-bold ${improvement > 0 ? 'text-green-600' : improvement < 0 ? 'text-red-500' : 'text-slate-400'}`}>
            {improvement > 0 ? '+' : ''}{improvement} pts
          </span>
        )}
      </div>

      {/* Simple bar chart */}
      <div className="flex items-end gap-1 h-16">
        {skillData.map((d, i) => {
          const pct = (d.score / maxScore) * 100;
          const color = d.score >= 7 ? 'bg-green-400' : d.score >= 4 ? 'bg-amber-400' : 'bg-red-400';
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
              <span className="text-[9px] font-bold text-slate-500">{d.score}</span>
              <div className={`w-full rounded-t-md ${color} transition-all`} style={{ height: `${Math.max(pct, 8)}%` }} />
              <span className="text-[8px] text-slate-400">#{d.session_number}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-1.5 pt-1 border-t border-slate-100">
        <AlertTriangle className="w-3 h-3 text-slate-400 mt-0.5 shrink-0" />
        <p className="text-[10px] text-slate-400 italic">AI scores are estimates and should not replace instructor assessment.</p>
      </div>
    </div>
  );
}
